import { twMerge } from 'tailwind-merge'
import { analyzeIntentDraft } from './analyzer'
import type { IntentChunkKind, IntentChunkAnalysis, WebMcpChunkPlan } from './types'
import type { RefinementContext } from '@/features/_shared/channel-types'
import { findElementInTree } from '@/features/page-editor/store/canvas-slice'
import { useEditorStore } from '@/features/page-editor/store/editor-store'
import { useFlowEditorStore } from '@/features/flow-editor/store/flow-editor-store'
import { useStudioStore } from '@/store/studio-store'
import {
  executeStudioCapabilityPlan,
  listPendingStudioCapabilities,
  listStudioCapabilityExecutions,
  type StudioCapabilityCommand,
  type StudioCapabilityImpactPreview,
} from '@/features/chat/services/studio-capability-gateway'
import {
  activateStudioGoldenJourneyStep,
  detectStudioGoldenJourneyIntent,
  getActiveStudioGoldenJourneySnapshot,
  startStudioGoldenJourney,
} from '@/features/chat/services/studio-golden-journeys'

export interface IntentChunkSubmitResult {
  handled: boolean
  analysis: IntentChunkAnalysis
  plan: WebMcpChunkPlan | null
  message?: string
  executedTool?: string
  executedTools?: string[]
  changedPaths?: string[]
  blocker?: string
  confirmationId?: string
  confirmationExpiresAt?: string
  confirmationImpact?: StudioCapabilityImpactPreview
  executionId?: string
}

export interface SubmitIntentChunkPlanOptions {
  refinementContext?: RefinementContext
}

const BLUE_BUTTON_CLASS = 'bg-blue-600 text-white hover:bg-blue-700'
const CHECKOUT_FLOW_NAME = 'checkoutFlow'
const LOCAL_RULE_EXECUTION_CONFIDENCE = 0.58

export async function submitIntentChunkPlan(
  input: string,
  options: SubmitIntentChunkPlanOptions = {},
): Promise<IntentChunkSubmitResult> {
  const analysis = await analyzeIntentDraft(input)
  const journeyIntent = detectStudioGoldenJourneyIntent(input)
  if (journeyIntent) {
    if (journeyIntent.action === 'start') {
      startStudioGoldenJourney(journeyIntent.journeyId)
    }
    const snapshot = getActiveStudioGoldenJourneySnapshot({
      pending: listPendingStudioCapabilities(),
      executions: listStudioCapabilityExecutions(),
    })
    if (snapshot) {
      activateStudioGoldenJourneyStep(snapshot)
      return {
        handled: true,
        analysis,
        plan: null,
        executedTool:
          journeyIntent.action === 'start'
            ? 'cogo.studio.start_golden_journey'
            : journeyIntent.action === 'inspect'
              ? 'cogo.studio.inspect_golden_journey'
              : 'cogo.studio.continue_golden_journey',
        message:
          journeyIntent.action === 'inspect'
            ? `${snapshot.definition.label}: ${snapshot.completedStepCount}/${snapshot.totalStepCount} steps complete. ${snapshot.nextPrompt}`
            : `${snapshot.definition.label} is active. ${snapshot.nextPrompt}`,
      }
    }
  }
  const plan = analysis.plan
  if (!plan || analysis.status !== 'ready') {
    return { handled: false, analysis, plan }
  }

  const blockingPlanIssue = plan.blockers.find((blocker) =>
    ['knowledge-gap', 'missing-tool', 'low-confidence'].includes(blocker.reason),
  )
  if (blockingPlanIssue) {
    return {
      handled: false,
      analysis,
      plan,
      blocker: blockingPlanIssue.reason,
      message: blockingPlanIssue.message,
    }
  }

  const target = resolveElementTarget(options.refinementContext)
  const commands: StudioCapabilityCommand[] = []
  const changedPaths = new Set<string>()
  const hasStylePrimary = hasReadyChunk(plan, 'style', 'style.change.color.primary')
  const hasCopyChange = hasReadyChunk(plan, 'copy', 'copy.change.text')
  const hasActionFlowCheckout = hasReadyChunk(plan, 'actionflow', 'actionflow.connect.checkout')
  const hasDataBind = hasReadyChunk(plan, 'data', 'data.bind.datasource')
  const hasPageMeta = hasReadyChunk(plan, 'operation', 'document.page.updateMeta')
  const hasPublish = hasReadyChunk(plan, 'operation', 'studio.publish.project')
  const hasSourceExport = hasReadyChunk(plan, 'operation', 'figma.source.exportOriginal')
  const hasSourceEdit = hasReadyChunk(plan, 'operation', 'figma.source.editRevision')
  const hasSourceMapping = hasReadyChunk(plan, 'operation', 'figma.source.applyMapping')
  const hasSourceTokens = hasReadyChunk(plan, 'operation', 'figma.source.decideTokens')
  const hasSourceVisualReview = hasReadyChunk(plan, 'operation', 'figma.source.visualReview')
  const hasSourceAssembly = hasReadyChunk(plan, 'operation', 'figma.source.buildAssembly')
  const hasTarget =
    Boolean(target) ||
    plan.chunks.some((chunk) => chunk.kind === 'target' && chunk.confidence >= 0.58)

  if (hasPageMeta) {
    const pageId = resolvePageId(input)
    const pageMeta = extractPageMeta(input)
    if (!pageId || Object.keys(pageMeta).length === 0) {
      return {
        handled: false,
        analysis,
        plan,
        blocker: 'ambiguous-target',
      }
    }
    commands.push({
      tool: 'cogo.studio.updatePageMeta',
      params: { pageId, ...pageMeta },
      requiresConfirmation: true,
      expectedChangedPaths: Object.keys(pageMeta).map((key) => `/manifest/pages/${pageId}/${key}`),
    })
    for (const key of Object.keys(pageMeta)) changedPaths.add(`/manifest/pages/${pageId}/${key}`)
  }

  if (hasStylePrimary || hasCopyChange) {
    if (!hasTarget || !target) {
      return {
        handled: false,
        analysis,
        plan,
        blocker: 'ambiguous-target',
      }
    }
    const changes: Record<string, unknown> = {}
    if (hasStylePrimary) {
      changes.className = twMerge(target.className, BLUE_BUTTON_CLASS)
      changedPaths.add(`/blocks/${target.blockName}/ui/${target.elementId}/className`)
    }
    if (hasCopyChange) {
      const text = extractReplacementText(input)
      if (!text) {
        return {
          handled: false,
          analysis,
          plan,
          blocker: 'ambiguous-target',
        }
      }
      changes.text = text
      changedPaths.add(`/blocks/${target.blockName}/ui/${target.elementId}/text`)
    }
    const elementPaths = [...changedPaths].filter((path) =>
      path.includes(`/blocks/${target.blockName}/ui/${target.elementId}/`),
    )
    commands.push({
      tool: 'cogo.studio.updateElement',
      params: {
        blockName: target.blockName,
        elementId: target.elementId,
        changes,
      },
      requiresConfirmation: true,
      expectedChangedPaths: elementPaths,
    })
  }

  if (hasActionFlowCheckout) {
    if (!hasTarget || !target) {
      return {
        handled: false,
        analysis,
        plan,
        blocker: 'ambiguous-target',
      }
    }
    if (!checkoutFlowExists()) {
      commands.push({
        tool: 'cogo.studio.addFlow',
        params: {
          name: CHECKOUT_FLOW_NAME,
          trigger: 'event',
          preserveSurface: true,
        },
        requiresConfirmation: true,
        expectedChangedPaths: [`/blocks/${target.blockName}/flow/${CHECKOUT_FLOW_NAME}`],
      })
    }
    commands.push({
      tool: 'cogo.studio.addEventBinding',
      params: {
        blockName: target.blockName,
        eventKey: `${target.elementId}:click`,
        flow: CHECKOUT_FLOW_NAME,
        element: {
          elementId: target.elementId,
          eventType: 'click',
          binding: {
            flow: CHECKOUT_FLOW_NAME,
            params: {
              source: 'chat-intent-chunk',
            },
          },
        },
      },
      requiresConfirmation: true,
      expectedChangedPaths: [
        `/blocks/${target.blockName}/events/${target.elementId}:click`,
        `/blocks/${target.blockName}/flow/${CHECKOUT_FLOW_NAME}`,
      ],
    })
    changedPaths.add(`/blocks/${target.blockName}/events/${target.elementId}:click`)
    changedPaths.add(`/blocks/${target.blockName}/flow/${CHECKOUT_FLOW_NAME}`)
  }

  if (hasDataBind) {
    const blockName = target?.blockName ?? resolveActiveBlockName()
    if (!blockName) {
      return {
        handled: false,
        analysis,
        plan,
        blocker: 'ambiguous-target',
      }
    }
    const datasourceName = extractDatasourceName(input) ?? 'chatDataSource'
    commands.push({
      tool: 'cogo.studio.addDatasource',
      params: {
        blockName,
        name: datasourceName,
        type: 'rest',
        method: 'GET',
        url: '/api/data',
        status: 'needs-configuration',
        source: 'chat-intent-chunk',
      },
      requiresConfirmation: true,
      expectedChangedPaths: [`/blocks/${blockName}/data/${datasourceName}`],
    })
    changedPaths.add(`/blocks/${blockName}/data/${datasourceName}`)
  }

  if (hasPublish) {
    commands.push({
      tool: 'cogo.studio.publishProject',
      params: {},
      requiresConfirmation: true,
      expectedChangedPaths: ['/deployment'],
    })
    changedPaths.add('/deployment')
  }

  if (hasSourceExport) {
    commands.push({
      tool: 'cogo.studio.export_openpencil_source_file',
      params: {},
      requiresConfirmation: false,
    })
  }

  if (hasSourceEdit) {
    const sourceEdit = extractSourceEditPayload(input)
    if (!sourceEdit) {
      return {
        handled: false,
        analysis,
        plan,
        blocker: 'ambiguous-target',
        message:
          'Inspect the source first, then provide a bounded patch with the verified node ID, type, current name, reason, and operations.',
      }
    }
    commands.push({
      tool: 'cogo.studio.revise_openpencil_figma_source',
      params: sourceEdit,
      requiresConfirmation: true,
      expectedChangedPaths: ['/sourceWorkspace/bundle', '/sourceWorkspace/bundle/revisionLineage'],
    })
    changedPaths.add('/sourceWorkspace/bundle')
    changedPaths.add('/sourceWorkspace/bundle/revisionLineage')
  }

  if (hasSourceMapping) {
    commands.push({
      tool: 'cogo.studio.apply_openpencil_suggested_mapping',
      params: {
        actor: 'agent',
        reason: 'Confirmed source mapping from Studio chat.',
      },
      requiresConfirmation: true,
      expectedChangedPaths: [
        '/sourceWorkspace/pageDecisions',
        '/sourceWorkspace/sectionDecisions',
        '/sourceWorkspace/surfaceDecisions',
      ],
    })
    changedPaths.add('/sourceWorkspace/pageDecisions')
    changedPaths.add('/sourceWorkspace/sectionDecisions')
    changedPaths.add('/sourceWorkspace/surfaceDecisions')
  }

  if (hasSourceTokens) {
    const status = /(거절|reject)/i.test(input) ? 'rejected' : 'accepted'
    commands.push({
      tool: 'cogo.studio.set_all_openpencil_token_decisions',
      params: {
        status,
        actor: 'agent',
        reason: 'Confirmed source token decision from Studio chat.',
      },
      requiresConfirmation: true,
      expectedChangedPaths: ['/sourceWorkspace/tokenDecisions'],
    })
    changedPaths.add('/sourceWorkspace/tokenDecisions')
  }

  if (hasSourceVisualReview) {
    commands.push({
      tool: 'cogo.studio.request_openpencil_visual_judgement',
      params: {},
      requiresConfirmation: true,
      expectedChangedPaths: ['/sourceWorkspace/visualJudgements'],
    })
    changedPaths.add('/sourceWorkspace/visualJudgements')
  }

  if (hasSourceAssembly) {
    commands.push({
      tool: 'cogo.studio.build_openpencil_source_assembly',
      params: {},
      requiresConfirmation: true,
      expectedChangedPaths: ['/sourceWorkspace/assemblyManifest'],
    })
    changedPaths.add('/sourceWorkspace/assemblyManifest')
  }

  if (commands.length === 0) {
    const hasExecutableChunk =
      hasStylePrimary ||
      hasCopyChange ||
      hasActionFlowCheckout ||
      hasDataBind ||
      hasPageMeta ||
      hasSourceMapping ||
      hasSourceEdit ||
      hasSourceTokens ||
      hasSourceVisualReview ||
      hasSourceAssembly
    return {
      handled: false,
      analysis,
      plan,
      ...(hasExecutableChunk ? { blocker: 'ambiguous-target' } : {}),
    }
  }

  const gateway = await executeStudioCapabilityPlan({
    planId: plan.planId,
    label: analysis.summary,
    intent: input,
    source: 'chat-intent-chunk',
    commands,
  })
  if (gateway.status === 'confirmation-required') {
    return {
      handled: false,
      analysis,
      plan,
      blocker: 'requires-user-confirmation',
      confirmationId: gateway.confirmationId,
      confirmationExpiresAt: gateway.expiresAt,
      confirmationImpact: gateway.impact,
      executedTools: gateway.tools,
      changedPaths: gateway.changedPaths,
      message: 'Review and confirm this WebMCP change before execution.',
    }
  }
  if (gateway.status === 'failed') {
    return {
      handled: false,
      analysis,
      plan,
      blocker: 'mcp-failed',
      message: gateway.error,
    }
  }
  return {
    handled: true,
    analysis,
    plan,
    executionId: gateway.execution.executionId,
    executedTool: gateway.execution.tools.at(-1),
    executedTools: gateway.execution.tools,
    changedPaths: gateway.execution.changedPaths,
    message: `Applied ${analysis.summary} through WebMCP.`,
  }
}

function hasReadyChunk(
  plan: WebMcpChunkPlan,
  kind: IntentChunkKind,
  canonicalId?: string,
): boolean {
  return plan.chunks.some(
    (chunk) =>
      chunk.kind === kind &&
      (!canonicalId || chunk.canonicalId === canonicalId) &&
      chunk.confidence >= LOCAL_RULE_EXECUTION_CONFIDENCE,
  )
}

function checkoutFlowExists(): boolean {
  const studioProject = useStudioStore.getState().currentProject
  const flowEditor = useFlowEditorStore.getState()
  const alreadyExists =
    flowEditor.flows.some((flow) => flow.name === CHECKOUT_FLOW_NAME) ||
    Boolean(studioProject?.flows?.[CHECKOUT_FLOW_NAME]) ||
    Object.values(studioProject?.blocks ?? {}).some((block) => {
      const flows = ((block as { flow?: Record<string, unknown> }).flow ?? {}) as Record<
        string,
        unknown
      >
      return Boolean(
        flows[CHECKOUT_FLOW_NAME] ||
        (flows.flows as Record<string, unknown> | undefined)?.[CHECKOUT_FLOW_NAME],
      )
    })
  return alreadyExists
}

function resolveElementTarget(refinementContext?: RefinementContext): {
  blockName: string
  elementId: string
  className: string
  text: string
} | null {
  const editor = useEditorStore.getState()
  const blockName = refinementContext?.block_id ?? editor.selectedBlockName
  const elementId = refinementContext?.element_id ?? editor.selectedIds[0]
  if (!blockName || !elementId) return null

  const block = editor.blocks[blockName]
  const element = block ? findElementInTree(block.ui.root, elementId) : undefined
  if (!element) return null
  const className =
    typeof element?.className === 'string'
      ? element.className
      : (readString(refinementContext?.current_state.className) ?? '')
  const text =
    typeof element?.text === 'string'
      ? element.text
      : (readString(refinementContext?.current_state.text) ?? '')

  return { blockName, elementId, className, text }
}

function readString(value: unknown): string {
  return typeof value === 'string' ? value : ''
}

function resolveActiveBlockName(): string | null {
  const editor = useEditorStore.getState()
  if (editor.selectedBlockName && editor.blocks[editor.selectedBlockName])
    return editor.selectedBlockName
  const page = editor.app?.pages.find((candidate) => candidate.id === editor.activePageId)
  return page?.blocks.find((blockName) => Boolean(editor.blocks[blockName])) ?? null
}

function resolvePageId(input: string): string | null {
  const editor = useEditorStore.getState()
  const normalized = input.toLowerCase()
  const pages = editor.app?.pages ?? []
  const explicit = pages.find((page) => {
    const title =
      typeof (page as { title?: unknown }).title === 'string'
        ? String((page as { title?: string }).title).toLowerCase()
        : ''
    return normalized.includes(page.id.toLowerCase()) || (title && normalized.includes(title))
  })
  return explicit?.id ?? editor.activePageId ?? pages[0]?.id ?? null
}

function extractPageMeta(input: string): {
  title?: string
  description?: string
  route?: string
} {
  const result: { title?: string; description?: string; route?: string } = {}
  const route = input.match(/(?:route|경로)\s*(?:를|을|=|:)?\s*([/a-z0-9_-]+)/i)?.[1]
  if (route) result.route = route

  const description = extractAfterLabel(input, ['설명', 'description'])
  if (description) result.description = description

  const title =
    extractQuotedText(input) ??
    extractAfterLabel(input, ['제목', '타이틀', 'title']) ??
    (!route && !description ? extractReplacementText(input) : null)
  if (title && !description) result.title = title
  return result
}

function extractReplacementText(input: string): string | null {
  const quoted = extractQuotedText(input)
  if (quoted) return quoted

  const labelValue = extractAfterLabel(input, ['문구', '텍스트', '라벨', 'label', 'copy', 'text'])
  if (labelValue) return labelValue

  const korean = input
    .match(/(?:을|를)\s*([^,\n]+?)\s*(?:으로|로)\s*(?:바꾸|변경|수정)/)?.[1]
    ?.trim()
  if (korean) return cleanupExtractedText(korean)

  const english = input.match(/(?:change|set|rename).*?(?:to|as)\s+([^,\n.]+)/i)?.[1]?.trim()
  return english ? cleanupExtractedText(english) : null
}

function extractQuotedText(input: string): string | null {
  return input.match(/["'“”‘’]([^"'“”‘’]+)["'“”‘’]/)?.[1]?.trim() ?? null
}

function extractAfterLabel(input: string, labels: string[]): string | null {
  for (const label of labels) {
    const pattern = new RegExp(
      `${label}\\s*(?:을|를|은|는|=|:)?\\s*([^,\\n]+?)\\s*(?:으로|로)?\\s*(?:바꾸|변경|수정|해줘|해주세요|set|change|update|$)`,
      'i',
    )
    const match = input.match(pattern)?.[1]?.trim()
    if (match) return cleanupExtractedText(match)
  }
  return null
}

function cleanupExtractedText(value: string): string {
  return value
    .replace(/^(?:을|를|은|는|이|가)\s*/, '')
    .replace(/\s*(?:바꿔줘|변경해줘|수정해줘|해줘|해주세요)$/i, '')
    .replace(/\s*(?:으로|로)$/i, '')
    .replace(/^["'“”‘’]|["'“”‘’]$/g, '')
    .trim()
}

function extractDatasourceName(input: string): string | null {
  const match = input.match(
    /(?:datasource|데이터소스|데이터|api)\s*(?:이름|name|=|:)?\s*([a-zA-Z][a-zA-Z0-9_-]{2,})/,
  )
  return match?.[1] ?? null
}

function extractSourceEditPayload(input: string): Record<string, unknown> | null {
  const start = input.indexOf('{')
  const end = input.lastIndexOf('}')
  if (start < 0 || end <= start) return null
  try {
    const value = JSON.parse(input.slice(start, end + 1)) as unknown
    if (!value || typeof value !== 'object' || Array.isArray(value)) return null
    const record = value as Record<string, unknown>
    if (
      typeof record.reason !== 'string' ||
      !record.reason.trim() ||
      !Array.isArray(record.operations) ||
      record.operations.length === 0
    ) {
      return null
    }
    return {
      ...(typeof record.patchId === 'string' ? { patchId: record.patchId } : {}),
      actor: record.actor === 'human' ? 'human' : 'agent',
      reason: record.reason,
      operations: record.operations,
    }
  } catch {
    return null
  }
}
